import { Button } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import {
  EmployeeGroupRootPageDocument,
  EmployeeGroupUpdateDocument,
  SettingsEmployeeGroupFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  employeeGroup: SettingsEmployeeGroupFragment
  onClose: () => void
}

const EditModal = ({ onClose, employeeGroup }: Props) => {
  const [EmployeeGroupUpdate] = useMutation(EmployeeGroupUpdateDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployeeGroup,
  })
  const name = useLazyRef(() => new Field(employeeGroup.name, 'string')).current

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await EmployeeGroupUpdate({
      variables: {
        id: employeeGroup.id,
        name: name.value,
      },
      refetchQueries: [EmployeeGroupRootPageDocument],
    })
    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closePortal()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  return (
    <Modal
      title={i18n.t('settings.legal_entities.legal_entity.employee_group.edit.title')}
      illustration="group"
      description={i18n.t('settings.legal_entities.legal_entity.employee_group.edit.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button submit>
          {i18n.t('settings.legal_entities.legal_entity.employee_group.edit.submit')}
        </Button>
      }
    >
      <Text
        label={i18n.t('settings.legal_entities.legal_entity.employee_group.labels.name')}
        field={name}
      />
    </Modal>
  )
}

export default observer(EditModal)
